import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'

export const defaultConfig = {
  layoutType: 'vertical',

  theme: 'light',

  nav: 'vertical',

  layout: {
    mode: 'fluid',
    position: 'fixed',
  },

  topbar: {
    color: 'light',
  },

  menu: {
    color: 'dark',
  },

  // This option for only vertical (left Sidebar) layout
  sidenav: {
    size: 'default',
    user: false,
  },
}

const html = (document.getElementsByTagName('html')[0]) as HTMLHtmlElement

export const useLayoutStore = defineStore('layout', {
  state: () => {
    return {
      isRightSidebarOpen: false,
      config: useStorage('__CONFIG__', {
        layoutType: 'vertical',

        theme: 'light',

        nav: 'vertical',

        layout: {
          mode: 'fluid',
          position: 'fixed',
        },

        topbar: {
          color: 'light',
        },

        menu: {
          color: 'dark',
        },

        // This option for only vertical (left Sidebar) layout
        sidenav: {
          size: 'default',
          user: false,
        },
      }),
    }
  },
  actions: {
    init() {
      const layoutColor = html.getAttribute('data-bs-theme')
      this.config['theme'] = layoutColor !== null ? layoutColor : this.config.theme

      const layoutNav = html.getAttribute('data-layout')
      this.config['nav'] = layoutNav != null ? (layoutNav == 'topnav' ? 'horizontal' : 'vertical') : this.config.nav

      const layoutSize = html.getAttribute('data-layout-mode')
      this.config['layout']['mode'] = layoutSize !== null ? layoutSize : this.config.layout.mode

      const layoutMode = html.getAttribute('data-layout-position')
      this.config['layout']['position'] = layoutMode !== null ? layoutMode : this.config.layout.position

      const topbarColor = html.getAttribute('data-topbar-color')
      this.config['topbar']['color'] = topbarColor != null ? topbarColor : this.config.topbar.color

      const leftbarSize = html.getAttribute('data-sidenav-size')
      this.config['sidenav']['size'] = leftbarSize !== null ? leftbarSize : this.config.sidenav.size

      const sidebarUser = html.getAttribute('data-sidenav-user')
      this.config['sidenav']['user'] = sidebarUser !== null ? true : this.config.sidenav.user

      const menuColor = html.getAttribute('data-menu-color')
      this.config['menu']['color'] = menuColor !== null ? menuColor : this.config.menu.color

      if (html.getAttribute('data-layout') == 'topnav') {
        this.config.nav = 'horizontal'
      } else {
        this.config.nav = 'vertical'
      }

      if (this.config) {
        html.setAttribute('data-bs-theme', this.config.theme)
        html.setAttribute('data-layout-mode', this.config.layout.mode)
        html.setAttribute('data-menu-color', this.config.menu.color)
        html.setAttribute('data-topbar-color', this.config.topbar.color)
        html.setAttribute('data-layout-position', this.config.layout.position)
        if (this.config.nav == 'vertical') {
          let size = this.config.sidenav.size
          if (window.innerWidth <= 767) {
            size = 'full'
          } else if (window.innerWidth >= 767 && window.innerWidth <= 1140) {
            if (this.config.sidenav.size !== 'full' && this.config.sidenav.size !== 'fullscreen') {
              size = 'condensed'
            }
          }
          html.setAttribute('data-sidenav-size', size)
          if (this.config.sidenav.user && this.config.sidenav.user.toString() === 'true') {
            html.setAttribute('data-sidenav-user', 'true')
          } else {
            html.removeAttribute('data-sidenav-user')
          }
        }
      }
    },

    changeLayoutType(type: string) {
      this.config.layoutType = type
      this.setSwitchFromConfig()
      this.initSwitchListener()
    },

    changeMenuColor(color: string) {
      this.config.menu.color = color
      html.setAttribute('data-menu-color', color)
      this.setSwitchFromConfig()
    },

    changeLeftbarSize(size: string, save = true) {
      html.setAttribute('data-sidenav-size', size)
      if (save) {
        this.config.sidenav.size = size
        this.setSwitchFromConfig()
      }
    },

    changeLayoutMode(mode: string, save = true) {
      html.setAttribute('data-layout-mode', mode)
      if (save) {
        this.config.layout.mode = mode
        this.setSwitchFromConfig()
      }
    },

    changeLayoutPosition(position: string) {
      this.config.layout.position = position
      html.setAttribute('data-layout-position', position)
      this.setSwitchFromConfig()
    },

    changeLayoutColor(color: string) {
      this.config.theme = color
      html.setAttribute('data-bs-theme', color)
      this.setSwitchFromConfig()
    },

    changeTopbarColor(color: string) {
      this.config.topbar.color = color
      html.setAttribute('data-topbar-color', color)
      this.setSwitchFromConfig()
    },

    changeSidebarUser(showUser: boolean) {
      this.config.sidenav.user = showUser
      if (showUser) {
        html.setAttribute('data-sidenav-user', showUser.toString())
      } else {
        html.removeAttribute('data-sidenav-user')
      }
      this.setSwitchFromConfig()
    },

    resetTheme() {
      this.config.layoutType = defaultConfig.layoutType
      this.changeMenuColor(defaultConfig.menu.color)
      this.changeLeftbarSize(defaultConfig.sidenav.size)
      this.changeLayoutColor(defaultConfig.theme)
      this.changeLayoutMode(defaultConfig.layout.mode)
      this.changeLayoutPosition(defaultConfig.layout.position)
      this.changeTopbarColor(defaultConfig.topbar.color)
      this.changeSidebarUser(defaultConfig.sidenav.user)
      this.setSwitchFromConfig()
      this._adjustLayout()
    },

    initSwitchListener() {
      document.querySelectorAll<HTMLInputElement>('input[name=data-menu-color]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeMenuColor(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=data-sidenav-size]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeLeftbarSize(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=data-bs-theme]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeLayoutColor(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=data-layout-mode]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeLayoutMode(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=data-layout-position]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeLayoutPosition(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=data-layout]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeLayoutType(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=data-topbar-color]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeTopbarColor(element.value)
        })
      })

      document.querySelectorAll<HTMLInputElement>('input[name=sidebar-user]').forEach((element) => {
        element.addEventListener('change', () => {
          this.changeSidebarUser(element.checked)
        })
      })

      //TopBar Light Dark
      const themeColorToggle = document.getElementById('light-dark-mode')
      if (themeColorToggle) {
        themeColorToggle.addEventListener('click', () => {
          if (this.config.theme === 'light') {
            this.changeLayoutColor('dark')
          } else {
            this.changeLayoutColor('light')
          }
        })
      }

      const resetBtn = document.querySelector('#reset-layout')
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          this.resetTheme()
        })
      }

      const menuToggleBtn = document.querySelector('.button-toggle-menu')
      if (menuToggleBtn) {
        menuToggleBtn.addEventListener('click', () => {
          const configSize = this.config.sidenav.size
          const size = html.getAttribute('data-sidenav-size')

          if (size === 'full') {
            this.showBackdrop()
          } else {
            if (configSize === 'fullscreen') {
              if (size === 'fullscreen') {
                this.changeLeftbarSize(configSize === 'fullscreen' ? 'default' : configSize, false)
              } else {
                this.changeLeftbarSize('fullscreen', false)
              }
            } else {
              if (size === 'condensed') {
                this.changeLeftbarSize(configSize === 'condensed' ? 'default' : configSize, false)
              } else {
                this.changeLeftbarSize('condensed', false)
              }
            }
          }

          html.classList.toggle('sidebar-enable')
        })
      }

      const horizontalMenuToggleBtn = document.querySelector('.horizontal-button-toggle-menu')
      if (horizontalMenuToggleBtn) {
        const horizontalMenu = document.getElementById('topnav-menu-content')
        horizontalMenuToggleBtn.addEventListener('click', () => {
          if (horizontalMenu?.classList.contains('show')) {
            return horizontalMenu?.classList.remove('show')
          }
          horizontalMenu?.classList.add('show')
        })
      }

      const menuCloseBtn = document.querySelector('.button-close-fullsidebar')
      if (menuCloseBtn) {
        menuCloseBtn.addEventListener('click', () => {
          html.classList.remove('sidebar-enable')
          this.hideBackdrop()
        })
      }

      const hoverBtn = document.querySelectorAll('.button-sm-hover')
      hoverBtn.forEach((element) => {
        element.addEventListener('click', () => {
          const size = html.getAttribute('data-sidenav-size')

          if (size === 'sm-hover-active') {
            this.changeLeftbarSize('sm-hover', false)
          } else {
            this.changeLeftbarSize('sm-hover-active', false)
          }
        })
      })
    },

    showBackdrop() {
      const backdrop = document.createElement('div')
      backdrop.id = 'custom-backdrop'
      backdrop.classList.add('offcanvas-backdrop', 'fade', 'show')
      document.body.appendChild(backdrop)
      document.body.style.overflow = 'hidden'
      if (window.innerWidth > 767) {
        document.body.style.paddingRight = '15px'
      }
      backdrop.addEventListener('click', () => {
        html.classList.remove('sidebar-enable')
        this.hideBackdrop()
      })
    },

    hideBackdrop() {
      const backdrop = document.getElementById('custom-backdrop')
      if (backdrop) {
        document.body.removeChild(backdrop)
        document.body.style.overflow = ''
        document.body.style.paddingRight = ''
      }
    },

    initWindowSize() {
      window.addEventListener('resize', () => {
        this._adjustLayout()
      })
    },

    _adjustLayout() {
      if (window.innerWidth <= 767.98) {
        this.changeLeftbarSize('full', false)
      } else if (window.innerWidth >= 767 && window.innerWidth <= 1140) {
        if (this.config.sidenav.size !== 'full' && this.config.sidenav.size !== 'fullscreen') {
          if (this.config.sidenav.size === 'sm-hover') {
            this.changeLeftbarSize('condensed')
          } else {
            this.changeLeftbarSize('condensed', false)
          }
        }
      } else {
        this.changeLeftbarSize(this.config.sidenav.size)
        this.changeLayoutMode(this.config.layout.mode)
      }
    },

    setSwitchFromConfig() {
      document.querySelectorAll<HTMLInputElement>('.right-bar input[type=checkbox]').forEach(function (checkbox) {
        checkbox.checked = false
      })

      const layoutColorSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-bs-theme][value=' + this.config.theme + ']',
      )
      const layoutModeSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-layout-mode][value=' + this.config.layout.mode + ']',
      )
      const topbarColorSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-topbar-color][value=' + this.config.topbar.color + ']',
      )
      const menuColorSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-menu-color][value=' + this.config.menu.color + ']',
      )
      const leftbarSizeSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-sidenav-size][value=' + this.config.sidenav.size + ']',
      )
      const layoutSizeSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-layout-position][value=' + this.config.layout.position + ']',
      )
      const sidebarUserSwitch = document.querySelector<HTMLInputElement>('input[type=checkbox][name=sidebar-user]')
      const layoutNavSwitch = document.querySelector<HTMLInputElement>(
        'input[type=radio][name=data-layout][value=' + this.config.layoutType + ']',
      )

      if (layoutColorSwitch) layoutColorSwitch.checked = true
      if (layoutModeSwitch) layoutModeSwitch.checked = true
      if (topbarColorSwitch) topbarColorSwitch.checked = true
      if (menuColorSwitch) menuColorSwitch.checked = true
      if (leftbarSizeSwitch) leftbarSizeSwitch.checked = true
      if (layoutSizeSwitch) layoutSizeSwitch.checked = true
      if (sidebarUserSwitch && this.config.sidenav.user.toString() === 'true') sidebarUserSwitch.checked = true
      if (layoutNavSwitch) layoutNavSwitch.checked = true
    },

    initConfig() {
      this.initSwitchListener()
      this.initWindowSize()
      this._adjustLayout()
      this.setSwitchFromConfig()
    },
  },
})
